// Client-side telemetry adapter → backend ClickHouse via /public/telemetry/client.
//
// Captures API/auth errors, failed mutations, and uncaught JS exceptions and
// ships them to the backend (worker-logger → ClickHouse db_logs_frontend).
//
// Design constraints:
//   - Best-effort: never throws into app code, never blocks a request.
//   - Recursion-safe: ships with a RAW fetch (not the axios instance), so it
//     can't loop through the 401-refresh interceptor, and it never logs its own
//     ingest call.
//   - Batched: events buffer and flush together (timer / size / page-hide), so
//     an error storm doesn't become a request storm.
//   - Throttled: identical events within a window are dropped (a single expired
//     token can otherwise emit dozens of 401s).

const API_BASE = import.meta.env?.DEV
	? "/api/v1"
	: "https://api.bodhistreams.com";
const INGEST_PATH = "/public/telemetry/client";
const INGEST_URL = `${API_BASE}${INGEST_PATH}`;
// Shared with activityStore — read-only here (we never create the session).
const SESSION_KEY = "bs:activity:session_id";

const MAX_BUFFER = 50; // hard cap on queued events (load-shed beyond this)
const FLUSH_AT = 10; // flush once the buffer reaches this many
const FLUSH_INTERVAL_MS = 4000;
const DEDUPE_MS = 30_000;
const DEDUPE_MAX_KEYS = 500;

const config = { app: "unknown", release: "", getUserId: () => "" };
let buffer = [];
const lastSent = new Map(); // dedupe key → last-sent epoch ms
let timer = null;
let installed = false;

/**
 * Set per-app identity. Call once at app startup (in main.jsx).
 * @param {Object} opts
 * @param {string} [opts.app] "app" | "admin" | "landing"
 * @param {string} [opts.release] build/version tag
 * @param {() => string} [opts.getUserId] optional resolver for the current user id
 */
export function configureClientLogger(opts = {}) {
	if (opts.app) config.app = opts.app;
	if (opts.release) config.release = opts.release;
	if (typeof opts.getUserId === "function") config.getUserId = opts.getUserId;
}

/**
 * The current client session id, or "" if none. Shared with activityStore (which
 * owns creating it) and used to correlate frontend events with backend `auth`
 * telemetry via the `X-Session-Id` header.
 * @returns {string}
 */
export function getSessionId() {
	return readSessionId();
}

function readSessionId() {
	try {
		return (
			(typeof window !== "undefined" &&
				window.sessionStorage.getItem(SESSION_KEY)) ||
			""
		);
	} catch {
		return "";
	}
}

function scheduleFlush() {
	if (timer || typeof window === "undefined") return;
	timer = setTimeout(() => {
		timer = null;
		flush();
	}, FLUSH_INTERVAL_MS);
}

/**
 * Queue a client event. Safe to call from anywhere; silently no-ops server-side
 * or when given nothing useful.
 * @param {Object} evt
 * @param {string} evt.kind required — "api_error" | "auth_error" | "query_error" | "js_error" | "unhandled_rejection"
 * @param {string} [evt.level] "error" | "warn" | "info"
 * @param {string} [evt.message]
 * @param {number} [evt.status] HTTP status (api/auth errors)
 * @param {string} [evt.method]
 * @param {string} [evt.url]
 * @param {string} [evt.stack]
 * @param {Object} [evt.meta]
 */
export function logClientEvent(evt = {}) {
	if (typeof window === "undefined" || !evt || !evt.kind) return;
	// Never log our own ingest call (would loop).
	if (evt.url?.includes(INGEST_PATH)) return;

	const key = `${evt.kind}|${evt.status ?? ""}|${evt.url ?? ""}|${evt.message ?? ""}`;
	const now = Date.now();
	const prev = lastSent.get(key);
	if (prev && now - prev < DEDUPE_MS) return;
	if (lastSent.size > DEDUPE_MAX_KEYS) lastSent.clear();
	lastSent.set(key, now);

	if (buffer.length >= MAX_BUFFER) return; // shed rather than grow unbounded

	buffer.push({
		kind: String(evt.kind),
		level: evt.level || "error",
		message: evt.message != null ? String(evt.message) : "",
		status: Number.isFinite(evt.status) ? evt.status : 0,
		method: evt.method || "",
		url:
			evt.url ||
			(typeof window !== "undefined" ? window.location.pathname : ""),
		stack: evt.stack || "",
		user_id: config.getUserId() || evt.user_id || "",
		session_id: readSessionId(),
		app: config.app,
		release: config.release,
		meta: evt.meta != null ? evt.meta : undefined,
	});

	if (buffer.length >= FLUSH_AT) flush();
	else scheduleFlush();
}

/**
 * Send any buffered events. Uses sendBeacon on page hide (so unload doesn't
 * drop the buffer), a keepalive fetch otherwise. Never throws.
 * @param {boolean} [useBeacon]
 */
export function flush(useBeacon = false) {
	if (typeof window === "undefined" || buffer.length === 0) return;
	const events = buffer;
	buffer = [];
	if (timer) {
		clearTimeout(timer);
		timer = null;
	}
	const body = JSON.stringify({ events });
	try {
		if (useBeacon && navigator.sendBeacon) {
			navigator.sendBeacon(
				INGEST_URL,
				new Blob([body], { type: "application/json" }),
			);
			return;
		}
		fetch(INGEST_URL, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body,
			credentials: "include",
			keepalive: true,
		}).catch(() => {});
	} catch {
		// telemetry must never throw into app code
	}
}

/**
 * Install window-level handlers for uncaught errors and unhandled promise
 * rejections, plus page-hide flushing. Idempotent. Call once at app startup.
 */
export function installGlobalErrorHandlers() {
	if (installed || typeof window === "undefined") return;
	installed = true;

	window.addEventListener("error", (e) => {
		logClientEvent({
			kind: "js_error",
			message: e?.message || "uncaught error",
			url: window.location.href,
			stack: e?.error?.stack || "",
			meta: { filename: e?.filename, lineno: e?.lineno, colno: e?.colno },
		});
	});

	window.addEventListener("unhandledrejection", (e) => {
		const reason = e?.reason;
		logClientEvent({
			kind: "unhandled_rejection",
			message: reason?.message || String(reason ?? "unhandled rejection"),
			url: window.location.href,
			stack: reason?.stack || "",
		});
	});

	window.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "hidden") flush(true);
	});
	window.addEventListener("pagehide", () => flush(true));
}
